import { ConnectError, Code } from '@connectrpc/connect';
import { workQueueService } from '@/core/api/services';
import {
  ActionValidationError,
  type BladeActionSource,
  type ActionSubmitResult,
} from '../BladeActionSource';
import { ACTION_TRANSITION_STATUS, ACTION_CLAIM, type ActionDescriptor } from '../actions';

// Live @hub/api write source (HUB-407) — dispatches an ActionDescriptor to the
// WorkQueueService mutation RPCs. The counterpart to liveSource (the read side).
//
// PHI: submitted values may reference work-item domain fields. This MUST NOT log or
// persist them; only the work-item id (an identifier, not a payload) is used for the
// RPC and cache invalidation. Server-side authz + lifecycle validation is
// authoritative — an illegal transition or bad value comes back as a ConnectRPC
// FailedPrecondition/InvalidArgument, which we surface as an inline ActionValidationError
// (not a blade error); permission/auth failures propagate as a ConnectError the hook
// classifies as "denied". PHI-Touching — needs security review before merge.

/** Strip the ConnectRPC `[code]` prefix so the raw server message renders cleanly. */
function serverMessage(e: ConnectError): string {
  return e.rawMessage || e.message;
}

/** Map a thrown RPC error to what the hook expects: pass ActionValidationError
 *  through untouched; turn an invalid-submit ConnectError (the server rejected the
 *  value or the lifecycle transition) into an inline field-level validation error;
 *  let permission/auth (and anything unexpected) propagate for the hook to classify. */
function mapActionError(e: unknown): unknown {
  if (e instanceof ActionValidationError) return e;
  if (e instanceof ConnectError) {
    if (e.code === Code.FailedPrecondition || e.code === Code.InvalidArgument) {
      // These are almost always about the chosen status (illegal transition or
      // unknown value), so render the server's reason against the status field.
      return new ActionValidationError(serverMessage(e), { status: serverMessage(e) });
    }
  }
  return e;
}

export const liveActionSource: BladeActionSource = {
  async submitAction(
    descriptor: ActionDescriptor,
    values: Record<string, string>,
  ): Promise<ActionSubmitResult> {
    const id = descriptor.entityId;
    if (!id) {
      throw new ActionValidationError('No work item is bound to this action.');
    }
    try {
      switch (descriptor.kind) {
        case ACTION_TRANSITION_STATUS: {
          const newStatus = (values.status ?? '').trim();
          if (!newStatus) {
            throw new ActionValidationError('Choose a status.', { status: 'Choose a status.' });
          }
          await workQueueService.transitionStatus({ id, newStatus });
          return { invalidateEntityIds: [id] };
        }
        case ACTION_CLAIM: {
          await workQueueService.claimWorkItem({ id });
          return { invalidateEntityIds: [id] };
        }
        default:
          throw new ActionValidationError(`Unknown action "${descriptor.kind}".`);
      }
    } catch (e) {
      throw mapActionError(e);
    }
  },
};
